import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Textarea,
  useColorModeValue,
  HStack,
  useToast,
  useDisclosure,
  Input,
  IconButton,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import {
  FiSave,
  FiZap,
} from 'react-icons/fi';
import TagSelector from './TagSelector';
import MediaUploader from './MediaUploader';
import MoodSelector from './MoodSelector';
import CustomFieldsEditor from './CustomFieldsEditor';
import { JournalEntry, Location, Weather } from '../../../types/journal';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useEncryption } from '../../../contexts/EncryptionContext';
import axios from 'axios';
import env from 'config/env';

interface JournalEntryProps {
  entry: JournalEntry | null;
  onSave: (entryData: JournalEntryData) => Promise<void>;
}

interface JournalEntryData {
  content: string;
  mood_label?: string;
  tags: string[];
  location?: Location;
  weather?: Weather;
  custom_fields: Array<{ id: string; value: any }>;
  entry_date: string;
}

const JournalEditor: React.FC<JournalEntryProps> = ({ entry, onSave }) => {
  const [content, setContent] = useState('');
  const [mood, setMood] = useState<{ label: string } | undefined>(undefined);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined);
  const [selectedWeather, setSelectedWeather] = useState<Weather | undefined>(undefined);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [entryDate, setEntryDate] = useState<Date>(new Date());
  const [isSaving, setIsSaving] = useState(false);
  const [isDetectingMood, setIsDetectingMood] = useState(false);
  const { isOpen: isUploaderOpen, onOpen: onUploaderOpen, onClose: onUploaderClose } = useDisclosure();
  const toast = useToast();
  const { subscription } = useSubscription();
  const { decrypt, encrypt } = useEncryption();

  const formatDateForInput = (date: Date): string => {
    // Format as local datetime string YYYY-MM-DDTHH:mm
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateFromInput = (value: string): Date => {
    // Parse the local datetime string directly
    return new Date(value);
  };

  useEffect(() => {
    if (entry) {
      setContent(entry.decrypted_content || '');
      setMood(entry.decrypted_mood_label ? { label: entry.decrypted_mood_label } : undefined);
      setSelectedTags(entry.tags || []);
      setSelectedLocation(
        entry.location_lat && entry.location_lng
          ? { lat: entry.location_lat, lng: entry.location_lng }
          : undefined
      );
      setSelectedWeather(entry.weather_data);
      setEntryDate(entry.entry_date ? new Date(entry.entry_date) : new Date());
    } else {
      setContent('');
      setMood(undefined);
      setSelectedTags([]);
      setSelectedLocation(undefined);
      setSelectedWeather(undefined);
      setEntryDate(new Date());
    }
  }, [entry]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        content,
        mood_label: mood?.label,
        tags: selectedTags,
        location: selectedLocation,
        weather: selectedWeather,
        custom_fields: Object.entries(customFieldValues).map(([id, value]) => ({
          id,
          value,
        })),
        entry_date: entryDate.toISOString(),
      });
      toast({
        title: 'Success',
        description: 'Journal entry saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save journal entry',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCustomFieldChange = (fieldId: string, value: any) => {
    setCustomFieldValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleTagsChange = async (newTags: string[]) => {
    setSelectedTags(newTags);
    // Auto-save when tags are updated
    try {
      await onSave({
        content,
        mood_label: mood?.label,
        tags: newTags,
        location: selectedLocation,
        weather: selectedWeather,
        custom_fields: Object.entries(customFieldValues).map(([id, value]) => ({
          id,
          value,
        })),
        entry_date: entryDate.toISOString(),
      });
    } catch (error) {
      console.error('Error auto-saving after tag update:', error);
    }
  };

  const handleDetectMood = async () => {
    if (!content.trim()) {
      toast({
        title: 'No content',
        description: 'Please write some content first to detect mood.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsDetectingMood(true);
    try {
      // First get all existing moods
      const moodsResponse = await axios.get(`${env.API_URL}/moods`);
      const decryptedMoods = await Promise.all(
        moodsResponse.data.map(async (encrypted: string) => {
          try {
            return await decrypt(encrypted);
          } catch (error) {
            console.error('Error decrypting mood:', error);
            return null;
          }
        })
      );
      const validDecryptedMoods = decryptedMoods.filter((m): m is string => m !== null);

      // Now detect the mood with context of existing moods
      const response = await axios.post(`${env.API_URL}/ai/detect-mood`, {
        content: content.trim(),
        existingMoods: validDecryptedMoods
      });

      const { mood: detectedMood, isNewMood, explanation, color } = response.data;

      if (isNewMood) {
        try {
          // Encrypt the new mood
          const encryptedMood = await encrypt(detectedMood);
          
          // Save the new mood to the database
          await axios.post(`${env.API_URL}/moods`, {
            encrypted_name: encryptedMood,
            color: color
          });

          // Set the mood with both the label and encrypted value
          setMood({ label: detectedMood });
        } catch (error) {
          console.error('Error saving new mood:', error);
          toast({
            title: 'Error',
            description: 'Failed to save new mood.',
            status: 'error',
            duration: 3000,
          });
          return;
        }
      } else {
        setMood({ label: detectedMood });
      }
      
      const toastDescription = isNewMood
        ? `Created new mood: ${detectedMood}\n${explanation}`
        : `Detected mood: ${detectedMood}\n${explanation}`;

      toast({
        title: isNewMood ? 'New Mood Created' : 'Mood Detected',
        description: toastDescription,
        status: 'success',
        duration: 5000,
      });

      // If a new mood was created, refresh the moods list
      if (isNewMood) {
        await refreshMoods();
      }
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast({
          title: 'Premium Feature',
          description: 'Automatic mood detection is only available for premium subscribers.',
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to detect mood.',
          status: 'error',
          duration: 3000,
        });
      }
    } finally {
      setIsDetectingMood(false);
    }
  };

  // Function to refresh moods list in MoodSelector
  const refreshMoods = useCallback(async () => {
    try {
      // This will trigger the useEffect in MoodSelector to reload moods
      const event = new CustomEvent('refreshMoods');
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error refreshing moods:', error);
    }
  }, []);

  return (
    <Flex direction="column" height="100%">
      <HStack spacing={4} mb={4}>
        <Button
          leftIcon={<FiSave />}
          colorScheme="blue"
          onClick={handleSave}
          isLoading={isSaving}
        >
          Save
        </Button>

        {/* <IconButton
          aria-label="Attach media"
          icon={<FiPaperclip />}
          onClick={onUploaderOpen}
        /> */}

        <Box flex="1" />

        <Input
          type="datetime-local"
          value={formatDateForInput(entryDate)}
          onChange={(e) => {
            const newDate = parseDateFromInput(e.target.value);
            setEntryDate(newDate);
          }}
          width="auto"
          size="sm"
        />
      </HStack>

      {/* Mood Selector */}
      <Box mb={4}>
        <MoodSelector
          value={mood}
          onChange={setMood}
          detectButton={
            <Tooltip 
              label={subscription?.isSubscribed 
                ? "Detect mood automatically" 
                : "Upgrade to premium for automatic mood detection"}
            >
              <IconButton
                aria-label="Detect mood"
                icon={isDetectingMood ? <Spinner size="sm" /> : <FiZap />}
                onClick={handleDetectMood}
                isDisabled={!content || isDetectingMood}
                variant="outline"
              />
            </Tooltip>
          }
        />
      </Box>

      {/* Tag Selector */}
      <Box mb={4}>
        <TagSelector
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
          content={content}
        />
      </Box>

      {/* Custom Fields */}
      <Box mb={4}>
        <CustomFieldsEditor
          journalEntryId={entry?.id}
          onFieldChange={handleCustomFieldChange}
        />
      </Box>

      {/* Editor */}
      <Textarea
        flex="1"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Write your thoughts here..."
        size="lg"
        resize="none"
        bg={useColorModeValue('white', 'gray.800')}
        border="1px solid"
        borderColor={useColorModeValue('gray.200', 'gray.600')}
        _focus={{
          borderColor: 'blue.500',
          boxShadow: 'outline',
        }}
        p={4}
        fontSize="md"
        lineHeight="tall"
      />

      {/* Media Uploader Modal */}
      <MediaUploader
        isOpen={isUploaderOpen}
        onClose={onUploaderClose}
        journalEntryId={entry?.id}
        onUploadComplete={() => {
          onUploaderClose();
        }}
      />
    </Flex>
  );
};

export default JournalEditor; 